/**
 * Client-Side Logger Wrapper
 *
 * Browser-compatible logger that wraps the core LoggerService.
 * Handles environment detection, console output, and optional remote logging.
 */

import { isSensitiveKey } from '@order-up/analytics/sentry';
import type { LogContext, LogLevel } from '@order-up/common';

// Lazy environment detection - deferred to first use to support both Vite and Next.js
const getNodeEnv = (): 'development' | 'production' => {
    // Check if in Next.js context (process available)
    if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
        return process.env.NODE_ENV as 'development' | 'production';
    }
    // Vite fallback - check import.meta.env.MODE
    try {
        const fn = new Function('return typeof import !== "undefined" && import.meta && import.meta.env');
        const importMetaEnv = fn();
        if (importMetaEnv && importMetaEnv.MODE) {
            return (importMetaEnv.MODE === 'production' ? 'production' : 'development') as 'development' | 'production';
        }
    } catch {
        // Fallback to development
    }
    return 'development';
};

const getLogConfig = () => {
    const nodeEnv = getNodeEnv();
    const isDevelopment = nodeEnv === 'development';

    // Check for process.env in Next.js
    let logEnabled = true;
    let logLevel: LogLevel = isDevelopment ? 'debug' : 'warn';

    if (typeof process !== 'undefined' && process.env) {
        logEnabled = process.env.NEXT_PUBLIC_LOG_ENABLED !== 'false';
        const envLogLevel = process.env.NEXT_PUBLIC_LOG_LEVEL;
        if (envLogLevel) {
            logLevel = envLogLevel as LogLevel;
        }
    }

    return { logEnabled, logLevel, isDevelopment };
};

const LOG_LEVELS: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
};

// Which keys are redacted is decided by the ONE shared list
// (`isSensitiveKey`, `@order-up/analytics` `sensitiveKeys.ts`) that core's logger
// and the Sentry scrubber read, so an SSN, date of birth or PIN is hidden here
// exactly as it is there. These three are browser-only additions on top of it.
const CLIENT_ONLY_SENSITIVE_FIELDS = ['cookie', 'session', 'handoff'];

/**
 * Redact sensitive data from objects
 */
function redactSensitive(obj: unknown, depth = 0): unknown {
    if (depth > 10) return '[MAX_DEPTH]';
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === 'string') {
        // Don't redact short strings
        if (obj.length > 20 && /^[A-Za-z0-9_-]+$/.test(obj)) {
            return '[REDACTED]';
        }
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => redactSensitive(item, depth + 1));
    }

    if (typeof obj === 'object') {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj)) {
            const lowerKey = key.toLowerCase();
            if (isSensitiveKey(key) || CLIENT_ONLY_SENSITIVE_FIELDS.some(field => lowerKey.includes(field))) {
                result[key] = '[REDACTED]';
            } else {
                result[key] = redactSensitive(value, depth + 1);
            }
        }
        return result;
    }

    return obj;
}

/**
 * Format output for browser console
 */
function formatBrowserOutput(
    level: LogLevel,
    message: string,
    context?: LogContext,
    _metadata?: Record<string, unknown>,
    _error?: unknown
): string[] {
    const timestamp = new Date().toLocaleTimeString();
    const service = context?.service ? `[${context.service}]` : '';
    const method = context?.method ? `.${context.method}` : '';
    const config = getLogConfig();

    const parts: string[] = [];

    // Main message with styling in development
    if (config.isDevelopment) {
        const levelColors: Record<LogLevel, string> = {
            debug: 'color: #6B7280',
            info: 'color: #3B82F6',
            warn: 'color: #F59E0B',
            error: 'color: #EF4444; font-weight: bold'
        };
        parts.push(
            `%c${timestamp} ${level.toUpperCase()} ${service}${method}`,
            levelColors[level],
            message
        );
    } else {
        parts.push(`${timestamp} ${level.toUpperCase()} ${service}${method} ${message}`);
    }

    return parts;
}

/**
 * Check if a log should be output
 */
function shouldLog(level: LogLevel): boolean {
    const config = getLogConfig();
    if (!config.logEnabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[config.logLevel];
}

export class ClientLogger {
    private context: LogContext;

    constructor(context: LogContext = {}) {
        this.context = context;
    }

    /**
     * Create a child logger with additional context
     */
    child(context: LogContext): ClientLogger {
        return new ClientLogger({ ...this.context, ...context });
    }

    /**
     * Log debug message (development only)
     */
    debug(message: string, metadata?: Record<string, unknown>): void {
        if (!shouldLog('debug')) return;

        const output = formatBrowserOutput('debug', message, this.context, metadata);
        console.debug(...output);

        if (metadata && Object.keys(metadata).length > 0) {
            console.debug('  Metadata:', redactSensitive(metadata));
        }
    }

    /**
     * Log info message
     */
    info(message: string, metadata?: Record<string, unknown>): void {
        if (!shouldLog('info')) return;

        const output = formatBrowserOutput('info', message, this.context, metadata);
        console.info(...output);

        if (metadata && Object.keys(metadata).length > 0) {
            console.info('  Metadata:', redactSensitive(metadata));
        }
    }

    /**
     * Log warning message
     */
    warn(message: string, metadata?: Record<string, unknown>): void {
        if (!shouldLog('warn')) return;

        const output = formatBrowserOutput('warn', message, this.context, metadata);
        console.warn(...output);

        if (metadata && Object.keys(metadata).length > 0) {
            console.warn('  Metadata:', redactSensitive(metadata));
        }
    }

    /**
     * Log error with optional error object
     */
    error(message: string, error?: Error | unknown, metadata?: Record<string, unknown>): void {
        if (!shouldLog('error')) return;

        const output = formatBrowserOutput('error', message, this.context, metadata, error);
        console.error(...output);

        if (error instanceof Error) {
            console.error('  Error:', error.name, '-', error.message);
            const config = getLogConfig();
            if (config.isDevelopment && error.stack) {
                console.error('  Stack:', error.stack);
            }
        } else if (error) {
            console.error('  Error:', error);
        }

        if (metadata && Object.keys(metadata).length > 0) {
            console.error('  Metadata:', redactSensitive(metadata));
        }
    }

    /**
     * Start a timed operation
     */
    startTimer(operation: string): () => void {
        // Use performance API if available (browser), otherwise Date.now()
        const perf = typeof performance !== 'undefined' ? performance : { now: () => Date.now() };
        const start = perf.now();
        return () => {
            const duration = Math.round(perf.now() - start);
            this.info(`${operation} completed`, { duration });
        };
    }
}

/**
 * Create a logger for a specific service/component
 */
export function createLogger(serviceName: string): ClientLogger {
    return new ClientLogger({ service: serviceName });
}

/**
 * Alias for createLogger (backwards compatibility)
 */
export const createClientLogger = createLogger;

/**
 * Default logger instance
 */
export const logger = new ClientLogger();
